import {GLOBAL} from "../../common/globals";
import {autoRegister, resolve} from "../../container";
import {Visibility} from "../../common/visibility";
import {isPresent} from "../../common/utils/basics";
import {forAllEntries, IntersectionObserverFactory} from "../../common/observation";
import {noop} from "../../common/utils/functions";
import type {Lifetime} from "../../common/lifetime";
import type {Maybe, Nullable} from "../../bootstrap/common/types";

const CONTENT_SECTION_CLASS = "content-section";
const SEPARATOR_SECTION_CLASS = "separator-section";
export const SECTION_BACKGROUND_CLASS_PREFIX = "section-background-";
export const VISIBLE_HEIGHT_TO_BECOME_ACTIVE = 175;

export namespace SectionFactory {
    export function from(section: Element): Nullable<Section> {
        if (isRelevantSection(section)) {
            const colorClass = contentSectionBackgroundClass(section.classList);
            if (colorClass) {
                return new Section(section, colorClass);
            }
        }
        return null;
    }

    function isRelevantSection(section: Element) : boolean {
        return section.classList.contains(CONTENT_SECTION_CLASS)
            || section.classList.contains(SEPARATOR_SECTION_CLASS)
            || section.tagName.toLowerCase() === "eop-chapter-navigation";
    }

    function contentSectionBackgroundClass(classList: DOMTokenList): Nullable<string> {
        for (const className of classList) {
            if (className.startsWith(SECTION_BACKGROUND_CLASS_PREFIX)) {
                return className;
            }
        }
        return null;
    }
}

export class Section {
    public constructor(
        public readonly element: Element,
        public readonly staticBackgroundColorClass: string
    ) {
    }
}

@autoRegister()
export class Sections {

    private sections: Section[];
    private intersectingSectionsObserver: IntersectionObserver;
    private fullyVisibleSectionsObserver: IntersectionObserver;

    private sectionChangedCallback: (section: Section) => void;

    public constructor(
        private visibility: Visibility = resolve(Visibility),
        private intersectionObserverFactory: IntersectionObserverFactory = resolve(IntersectionObserverFactory)
    ) {
        this.sections = this.determineSections();
        this.sectionChangedCallback = noop;
        this.intersectingSectionsObserver = this.intersectionObserverFactory.create(
            forAllEntries(() => this.reactToSectionChange()),
            {rootMargin: `-${VISIBLE_HEIGHT_TO_BECOME_ACTIVE}px 0px`}, "intersecting");
        this.fullyVisibleSectionsObserver = this.intersectionObserverFactory.create(forAllEntries(_ => this.reactToSectionChange()), {threshold: 1}, "fullyVisible");
    }

    private reactToSectionChange(): void {
        const activeSection = this.determineActiveSection();
        if (activeSection) {
            this.sectionChangedCallback(activeSection);
        }
    }

    public getSections(): Section[] {
        return this.sections;
    }

    public onSectionChange(callback: (section: Section) => void, lifetime?: Lifetime): void {
        this.sectionChangedCallback = callback;

        const sectionElements = this.sections
            .map(section => section.element)
            .filter(isPresent)
            .distinct();

        sectionElements.forEach(element => {
            this.intersectingSectionsObserver.observe(element);
            this.fullyVisibleSectionsObserver.observe(element);
        });
        lifetime?.onDrop(() => {
            this.intersectingSectionsObserver.disconnect();
            this.fullyVisibleSectionsObserver.disconnect();
        });
        this.reactToSectionChange();
    }

    private determineActiveSection(): Maybe<Section> {
        const candidates = this.sections
            .map(section => this.sectionVisibility(section))
            .filter(markedSection => markedSection.visibleEnough);

        const candidate = candidates.find(markedSection => markedSection.visibleCompletely)
            ?? candidates.firstElement();

        return candidate?.section;
    }

    private determineSections(): Section[] {
        const main = GLOBAL.bodyElement().querySelector("main")!;
        const sections: Section[] = [];
        for (const child of main.children) {
            const nextSection = SectionFactory.from(child);
            if (nextSection) {
                sections.push(nextSection);
            }
        }
        return sections;
    }

    private sectionVisibility(section: Section): SectionVisibility {
        if (!this.sectionIntersectsViewport(section.element)) {
            return {section: section, visibleEnough: false, visibleCompletely: false};
        }

        const visibleSectionHeight = this.visibility.getViewportIntersectionHeight(section.element);
        if (visibleSectionHeight < VISIBLE_HEIGHT_TO_BECOME_ACTIVE) {
            return {section: section, visibleEnough: false, visibleCompletely: false};
        }

        const insideViewport = this.visibility.isInsideViewport(section.element);
        return {section: section, visibleEnough: true, visibleCompletely: insideViewport};
    }

    private sectionIntersectsViewport(sectionElement: Element): boolean {
        return this.visibility.intersectsViewport(sectionElement);
    }
}

type SectionVisibility = {
    section: Section;
    visibleEnough: boolean;
    visibleCompletely: boolean;
}